import clsx from "clsx";
import { MouseEvent } from "react";
import {
  Icon,
  KeyValueList,
  RenderWhen,
  Time
} from "../../../../../../components";
import { GeneralInfo } from "../../../../../samples_cloud/@types";
import { SECTIONS } from "../../../../consts";
import {
  useFetchExtractedScreenshots,
  useLocalSampleSummaryParams
} from "../../../../hooks";
import { Loading } from "../../components";
import styles from "../analysis-card.module.scss";

type Props = {
  analysisId?: string;
  data?: GeneralInfo;
  isStored?: boolean;
  isPrivate?: boolean;
  onClick: (section: string) => void;
  onRefresh: () => void;
  onShowScreenshots: (event: MouseEvent) => void;
};

const DynamicAnalysisContent = ({
  analysisId,
  data,
  isStored,
  isPrivate,
  onClick,
  onRefresh,
  onShowScreenshots
}: Props) => {
  const { hash } = useLocalSampleSummaryParams();

  const { isError, isInitialLoading } = useFetchExtractedScreenshots(
    hash ?? "",
    analysisId ?? ""
  );

  const handleOnClick = (event: MouseEvent) => {
    event.stopPropagation();
    onClick(SECTIONS.DYNAMIC_ANALYSIS.CLOUD_SANDBOX);
  };

  return (
    <KeyValueList
      className={styles.analysis_card_content_wrapper}
      data-testid="report_summary_dynamic_analysis_list"
    >
      <KeyValueList.Item
        className={styles.list_item_dynamic_analysis}
        keyClassName={clsx("font-medium", "white-space-nowrap")}
        label="Analysis Time: "
        value={
          <Time
            className={clsx(styles.time, "overflow-ellipsis")}
            time={data?.last_analysis}
          />
        }
        onClick={handleOnClick}
        data-testid="report_summary_dynamic_analysis_analysis_time"
      />
      <KeyValueList.Item
        className={styles.list_item_dynamic_analysis}
        keyClassName={clsx("font-medium", "white-space-nowrap")}
        label="Risk Score: "
        value={<div className={styles.list_item_value}>{data?.risk_score}</div>}
        onClick={handleOnClick}
        data-testid="report_summary_dynamic_analysis_risk_score"
      />
      <KeyValueList.Item
        className={styles.list_item_dynamic_analysis}
        keyClassName={clsx("font-medium", "white-space-nowrap")}
        label="First Analysis: "
        value={
          <Time
            className={clsx(styles.time, "overflow-ellipsis")}
            time={data?.first_analysis}
          />
        }
        onClick={handleOnClick}
        data-testid="report_summary_dynamic_analysis_first_analysis"
      />
      <KeyValueList.Item
        className={styles.list_item_dynamic_analysis}
        keyClassName={clsx("font-medium", "white-space-nowrap")}
        label="Platforms: "
        value={<div className={"overflow-ellipsis"}>{data?.platforms}</div>}
        onClick={handleOnClick}
        data-testid="report_summary_dynamic_analysis_first_platforms"
      />
      <KeyValueList.Item
        className={clsx(styles.list_item)}
        keyClassName={"font-normal"}
        label={
          <RenderWhen
            condition={!isError}
            fallback={
              <span data-testid="report_summary_dynamic_analysis_no_screenshots">
                No Screenshots Available
              </span>
            }
          >
            <RenderWhen
              condition={!isPrivate && !isInitialLoading}
              fallback={<Loading />}
            >
              <span
                className={clsx("underline", "cursor-pointer")}
                onClick={onShowScreenshots}
                data-testid="report_summary_dynamic_analysis_see_screenshots"
              >
                See Screenshots
              </span>
            </RenderWhen>
          </RenderWhen>
        }
        value={<></>}
      />
      <RenderWhen condition={Boolean(isStored)}>
        <KeyValueList.Item
          className={clsx(styles.list_item)}
          keyClassName={"font-normal"}
          label={
            <div className="flex align-center flex-gap-1">
              <Icon name="warning-i" />
              <span className="max-content-width">Showing Cached Report</span>
              <span
                className={clsx("underline", "cursor-pointer")}
                onClick={onRefresh}
                data-testid="report_summary_dynamic_analysis_refetch_action"
              >
                (Refetch)
              </span>
            </div>
          }
          value={<></>}
        />
      </RenderWhen>
    </KeyValueList>
  );
};

export default DynamicAnalysisContent;
